/**
 * Alinhamento de séries mensais por COMPETÊNCIA (`MM/YYYY`).
 *
 * O gráfico rotula o eixo com `dates` e consome cada série **por índice**. Várias
 * séries, porém, eram construídas com `.map()` sobre o array de dados cru — em
 * ordem de chegada, e só com as competências que TÊM valor. Quando um mês faltava
 * na série, todos os valores seguintes deslizavam para o mês errado.
 *
 * Caso real (Prívia, abr/2024 a abr/2025): `A receber` tinha R$ 206.800,00 em
 * jun/2024 e R$ 187.095,71 em abr/2025, e nada nos demais meses.
 * `toReceiveBills.map(i => i.value)` produzia `[206800, 187095.71]`, que o eixo
 * lia como abr/2024 e mai/2024 — barra, variação e CSV deslocados.
 *
 * A regra deste módulo: **o eixo manda**. Toda série tem o comprimento do eixo e
 * o valor de cada posição vem da competência daquela posição, nunca da ordem do
 * array de origem.
 */

/**
 * Indexa itens `{date, value}` por competência.
 *
 * @param items lista de origem
 * @param dateKey  campo da competência (há série que usa `from`/`to`)
 * @param valueKey campo do valor (há série que usa `accumulated`)
 * @param aggregate `"sum"` para valor do mês (aditivo) ou `"last"` para
 *   acumulado — somar dois acumulados da mesma competência daria o dobro.
 */
export const indexByCompetence = (
  items = [],
  { dateKey = "date", valueKey = "value", aggregate = "sum" } = {}
) => {
  const totals = new Map();

  for (const item of items ?? []) {
    const date = item?.[dateKey];
    if (!date) continue;

    const value = item?.[valueKey];

    if (aggregate === "last") {
      totals.set(date, value);
      continue;
    }

    const current = totals.get(date);
    const increment = Number.isFinite(value) ? value : 0;
    totals.set(date, (Number.isFinite(current) ? current : 0) + increment);
  }

  return totals;
};

/**
 * Série alinhada ao eixo. Competência ausente recebe `fallback` (0 para valor do
 * mês; passe `undefined` quando a intenção for mostrar lacuna no gráfico).
 */
export const alignToAxis = (axis = [], items = [], options = {}) => {
  const { fallback: _ignored, ...indexOptions } = options;
  // ⚠️ `{ fallback = 0 }` no destructuring NÃO serve: default de destructuring
  // dispara para `undefined`, então `{fallback: undefined}` — que é justamente
  // como se pede "deixe lacuna" — voltaria a virar 0. Checar a presença da chave.
  const fallback = "fallback" in options ? options.fallback : 0;
  const byCompetence = indexByCompetence(items, indexOptions);

  return (axis ?? []).map((date) =>
    byCompetence.has(date) ? byCompetence.get(date) : fallback
  );
};

/**
 * Série acumulada alinhada ao eixo, com carry-forward explícito: mês sem
 * movimento repete o último acumulado conhecido.
 *
 * Antes do primeiro dado devolve `undefined`, não 0 — zero afirmaria que o
 * acumulado era zero, quando o que existe é ausência de dado.
 *
 * ⚠️ O código antigo fazia `if (!item) return` dentro do `forEach` do eixo, o que
 * **encurtava** o array em vez de preencher a lacuna: os pontos seguintes
 * deslizavam para meses anteriores.
 */
export const alignAccumulated = (axis = [], items = [], options = {}) => {
  const byCompetence = indexByCompetence(items, { ...options, aggregate: "last" });

  let last;
  return (axis ?? []).map((date) => {
    if (byCompetence.has(date)) {
      const value = byCompetence.get(date);
      // Só avança o carry quando há valor utilizável; um `null` no meio da série
      // não pode apagar o acumulado já conhecido.
      if (value !== null && value !== undefined) last = value;
    }
    return last;
  });
};
